import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  AfterViewInit,
} from '@angular/core';
import { DragDropModule } from '@angular/cdk/drag-drop';
import {
  numberOnlyValidator,
  createDateTimeStamp,
  isObjectEmpty,
} from 'src/app/shared/functions';
import {
  DistributorList,
  ModalID,
  StatusModalIndicator,
  ComponentNames,
} from 'src/app/shared/models/constants';
import { LiveVendorData } from 'src/app/shared/models/vendor';
// services
import {
  CompetitorPricing,
  SharedService,
} from 'src/app/shared/services/shared.service';
import { AgGridService } from 'src/app/shared/services/ag-grid.service';
// Ag-Grid
import {
  GridOptions,
  GridApi,
  ColDef,
  RowClickedEvent,
  RowSelectionOptions,
  CellContextMenuEvent,
} from 'ag-grid-community';
import { agGridDarkTheme } from 'src/app/shared/components/ag-grid/dark-theme';
// models
import { LiveInventoryColumnDef } from '../columnDefs';
import { DistributorService } from 'src/app/shared/services/distributor.service';
import {
  DistributorInventoryProfile,
  InventoryAlert,
  AutoPurchase,
} from 'src/app/shared/models/general';
import { debounceTime, Subject, Subscription } from 'rxjs';
import { NgClass, DatePipe, AsyncPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AgGridAngular } from 'ag-grid-angular';
import { ReplacePipe } from 'src/app/shared/pipes/replace.pipe';
import { CompetitorPricingComponent } from './modals/competitor-pricing/competitor-pricing.component';
import { ContextMenuService } from 'src/app/shared/services/context-menu.service';
import { InventoryRequestService } from '../inventory-request-lists/inventory-request.service';

interface FacetOption {
  value: string;
  label: string;
  count: number;
  selected: boolean;
}

interface FacetGroup {
  key: string;
  label: string;
  options: FacetOption[];
  visibleOptions: FacetOption[];
  hiddenCount: number;
  expanded: boolean;
  collapsed: boolean;
  selectedCount: number;
}

// Facets run wide on real data (model and caliber both clear 45 distinct
// values inside a single result page), so long lists are truncated to the
// most common values until the user asks for the rest.
const FACET_OPTION_LIMIT = 12;

const PRICE_BUCKETS = [
  { value: 'p0', label: 'Under $50', min: 0, max: 50 },
  { value: 'p1', label: '$50 - $100', min: 50, max: 100 },
  { value: 'p2', label: '$100 - $250', min: 100, max: 250 },
  { value: 'p3', label: '$250 - $500', min: 250, max: 500 },
  { value: 'p4', label: '$500 - $1,000', min: 500, max: 1000 },
  { value: 'p5', label: '$1,000+', min: 1000, max: Infinity },
];

// Distributor key used in the user's selected-distributor list -> qty/price
// field prefix on the inventory record. The two differ for Allen Arms.
const FACET_DISTRIBUTORS = [
  { key: 'allen_arms_tactical', prefix: 'allen_arms', label: 'Allen Arms' },
  { key: 'chattanooga', prefix: 'chattanooga', label: 'Chattanooga' },
  { key: 'davidsons', prefix: 'davidsons', label: 'Davidsons' },
  { key: 'lipseys', prefix: 'lipseys', label: 'Lipseys' },
  { key: 'rsr_group', prefix: 'rsr_group', label: 'RSR' },
  { key: 'zanders', prefix: 'zanders', label: 'Zanders' },
];

// Taxonomy facets render above the item's own attributes; the buying-related
// ones render below. Everything between is derived per result set.
const LEADING_FACET_DEFS = [
  { key: 'manufacturer', label: 'Manufacturer' },
  { key: 'category', label: 'Category' },
  { key: 'subcategory', label: 'Subcategory' },
  { key: 'item_type', label: 'Item Type' },
];

const TRAILING_FACET_DEFS = [
  { key: 'stock', label: 'In Stock At' },
  { key: 'availability', label: 'Availability' },
  { key: 'price', label: 'Price' },
];

const ATTRIBUTE_FACET_PREFIX = 'attr:';

@Component({
  selector: 'app-vendor-dashboard',
  templateUrl: './vendor-dashboard.component.html',
  styleUrls: ['./vendor-dashboard.component.scss'],
  standalone: true,
  imports: [AgGridAngular, AsyncPipe, NgClass, DatePipe, FormsModule, DragDropModule, ReplacePipe],
})
export class VendorDashboardComponent implements OnDestroy, OnInit {
  @ViewChild('dispo_list', { read: ElementRef }) gridElement!: ElementRef;
  subscriptions = new Subscription();
  item_upc: string;
  search_term: string;
  lastSyncTime: string | null = null;
  selectedRow: any;
  itemThumbnailUrls: string[] = [];
  currentImageIndex = 0;
  isLightboxOpen = false;
  isThumbnailLoading = false;
  showMenu = false;
  menuTop = 0;
  menuLeft = 0;
  distributorList = DistributorList;
  showBrowsePanel = false;
  browseTab: 'categories' | 'manufacturers' = 'categories';
  categoriesList: string[] = [];
  manufacturersList: string[] = [];
  categoriesLoaded = false;
  manufacturersLoaded = false;
  activeBrowse: { type: 'manufacturer' | 'category' | 'search' | 'favorites'; value: string } | null =
    null;
  showFilterSidebar = true;
  filterSidebarWidth = 240;
  isDraggingFilter = false;
  facetGroups: FacetGroup[] = [];
  filteredItemCount = 0;
  private collapsedFacets = new Set<string>();
  private expandedFacets = new Set<string>();
  private selectedFacets: Record<string, Set<string>> = {};
  private applyDefaultCollapse = true;
  private dragStartX = 0;
  private dragStartWidth = 0;
  selectedDistributor: any = null; // Adjust type based on your distributor object
  distributorDisplayData: {
    name: string;
    quantity: number;
    price: string | null;
  }[] = [];
  // Context Menu state from service
  menuState$ = this.contextMenuService.menuState$;

  private rowClickSubject = new Subject<RowClickedEvent>();

  // ag-grid properties Grid
  gridApi!: GridApi;

  columnDefs: ColDef[] = LiveInventoryColumnDef;
  defaultColDef: ColDef = {
    filter: true,
    floatingFilter: true,
    sortable: true,
    resizable: true,
    // Without this, plain-text cells anchor to the top of the row instead of
    // centering (rowHeight below is taller than a default single-line row).
    cellStyle: { display: 'flex', alignItems: 'center' },
  };
  overlayLoadingTemplate =
    '<span class="ag-overlay-loading-center">Please wait while your rows are loading</span>';
  gridOptions: GridOptions = {
    theme: agGridDarkTheme,
    context: { componentParent: this },
    headerHeight: 60,
    floatingFiltersHeight: 36,
    suppressHorizontalScroll: false,
    getRowId: (params) => params.data._id,
    enableCellTextSelection: true,
    ensureDomOrder: true,
    tooltipShowDelay: 300,
    tooltipInteraction: true,
    rowHeight: 80,
    rowClass: 'ag-grid-row',
  };
  components = {};
  theme = agGridDarkTheme;
  public rowSelection: RowSelectionOptions = {
    mode: 'singleRow',
    checkboxes: false,
  };

  constructor(
    public sharedService: SharedService,
    private distributorService: DistributorService,
    private contextMenuService: ContextMenuService,
    private inventoryRequestService: InventoryRequestService,
    private agGridService: AgGridService,
    private cdr: ChangeDetectorRef,
  ) {
    this.subscriptions.add(
      this.rowClickSubject.pipe(debounceTime(300)).subscribe((event) => {
        const rowData = event.data;
        this.selectedRow = event.data;
        const inventory_item = this.sharedService.item_data.find(
          (item) => item.upc === rowData.upc,
        );
        if (!inventory_item) {
          this.sharedService.raiseErrorToast(
            'No matching inventory item found',
          );
          return;
        }
        this.updateDistributorData(inventory_item);
        this.fetchThumbnail(
          rowData.upc,
          rowData.item_number,
          rowData.manufacturer,
          rowData.mfg_part_number,
          rowData.origin,
          rowData.imageSource,
        );
      }),
    );
  }
  ngAfterViewInit() {
    // Initialize service after gridElement is available
    this.contextMenuService.initialize(this.cdr, this.gridElement);
  }
  ngOnInit(): void {
    this.fetchLastSyncTime();
    this.loadBrowseData();
    this.resetFacets();
    if (this.distributorService.live_inv_selected_distributors.length === 0) {
      this.distributorService.getLiveInventoryDistributorList().then(() => {
        this.updateColumnVisibility();
      });
    } else {
      this.updateColumnVisibility();
    }
  }

  fetchLastSyncTime() {
    this.subscriptions.add(
      this.sharedService.getVendorInventoryLastSync().subscribe({
        next: (data) => {
          this.lastSyncTime = data.last_sync;
        },
        error: (err) => console.error('Failed to fetch last sync time:', err),
      }),
    );
  }

  openBrowsePanel(tab: 'categories' | 'manufacturers') {
    if (this.showBrowsePanel && this.browseTab === tab) {
      this.closeBrowsePanel();
      return;
    }
    this.browseTab = tab;
    this.showBrowsePanel = true;
    this.loadBrowseData();
  }

  closeBrowsePanel() {
    this.showBrowsePanel = false;
  }

  loadBrowseData() {
    if (!this.categoriesLoaded) {
      this.subscriptions.add(
        this.sharedService.getCategoriesList().subscribe((list) => {
          this.categoriesList = list;
          this.categoriesLoaded = true;
        }),
      );
    }
    if (!this.manufacturersLoaded) {
      this.subscriptions.add(
        this.sharedService.getManufacturersList().subscribe((list) => {
          this.manufacturersList = list;
          this.manufacturersLoaded = true;
        }),
      );
    }
  }

  get manufacturersGrouped(): { letter: string; items: string[] }[] {
    const groups: Record<string, string[]> = {};
    this.manufacturersList.forEach((name) => {
      const first = name.trim().charAt(0).toUpperCase();
      const letter = /[A-Z]/.test(first) ? first : '#';
      if (!groups[letter]) groups[letter] = [];
      groups[letter].push(name);
    });
    return Object.keys(groups)
      .sort()
      .map((letter) => ({ letter, items: groups[letter] }));
  }

  scrollToLetter(letter: string) {
    const el = document.getElementById('mfg-letter-' + letter);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  selectCategory(category: string) {
    this.closeBrowsePanel();
    this.browseSearch('category', category);
  }

  selectManufacturer(manufacturer: string) {
    this.closeBrowsePanel();
    this.browseSearch('manufacturer', manufacturer);
  }

  private browseSearch(type: 'manufacturer' | 'category', value: string) {
    this.item_upc = '';
    this.search_term = '';
    this.clearQuantitiesAndPrices();
    this.sharedService.openStatusSpinner(`Loading items for ${value}`);
    this.subscriptions.add(
      this.sharedService.searchInventoryAdvanced({ [type]: value }).subscribe({
        next: (items) => {
          this.sharedService.closeStatusSpinner();
          this.sharedService.item_data = [...items];
          this.activeBrowse = items.length ? { type, value } : null;
          this.resetFacets();
          this.updateColumnVisibility();
          this.itemThumbnailUrls = [];
          this.currentImageIndex = 0;
          if (!items.length) {
            this.sharedService.raiseErrorToast(`No items found for "${value}".`);
          }
        },
        error: () => {
          this.sharedService.closeStatusSpinner();
        },
      }),
    );
  }

  // ---- Attribute filter sidebar ----

  toggleFilterSidebar() {
    this.showFilterSidebar = !this.showFilterSidebar;
  }

  onFilterDragStart(event: MouseEvent) {
    event.preventDefault();
    this.isDraggingFilter = true;
    this.dragStartX = event.clientX;
    this.dragStartWidth = this.filterSidebarWidth;
    document.addEventListener('mousemove', this.onFilterDragMove);
    document.addEventListener('mouseup', this.onFilterDragEnd);
  }

  private onFilterDragMove = (event: MouseEvent) => {
    if (!this.isDraggingFilter) return;
    const delta = event.clientX - this.dragStartX;
    const newWidth = Math.max(80, Math.min(400, this.dragStartWidth + delta));
    this.filterSidebarWidth = newWidth;
  };

  private onFilterDragEnd = () => {
    this.isDraggingFilter = false;
    document.removeEventListener('mousemove', this.onFilterDragMove);
    document.removeEventListener('mouseup', this.onFilterDragEnd);
  };

  toggleFacetCollapse(key: string) {
    const collapsed = !this.collapsedFacets.has(key);
    if (collapsed) {
      this.collapsedFacets.add(key);
    } else {
      this.collapsedFacets.delete(key);
    }
    const group = this.facetGroups.find((g) => g.key === key);
    if (group) group.collapsed = collapsed;
  }

  toggleFacetExpanded(groupKey: string) {
    if (this.expandedFacets.has(groupKey)) {
      this.expandedFacets.delete(groupKey);
    } else {
      this.expandedFacets.add(groupKey);
    }
    this.rebuildFacets();
  }

  toggleFacetOption(groupKey: string, value: string) {
    const selected = (this.selectedFacets[groupKey] ??= new Set<string>());
    if (selected.has(value)) {
      selected.delete(value);
    } else {
      selected.add(value);
    }
    this.applyFacets();
  }

  clearAllFacets() {
    this.selectedFacets = {};
    this.applyFacets();
  }

  get hasActiveFacets(): boolean {
    return Object.values(this.selectedFacets).some((set) => set.size > 0);
  }

  private resetFacets() {
    this.selectedFacets = {};
    this.collapsedFacets.clear();
    this.expandedFacets.clear();
    this.applyDefaultCollapse = true;
    this.applyFacets();
  }

  private applyFacets() {
    this.rebuildFacets();
    this.gridApi?.onFilterChanged();
  }

  // Distributors the user has switched on; all of them if none are configured,
  // matching how the grid's distributor columns behave.
  private get facetDistributors() {
    const selected =
      this.distributorService.live_inv_selected_distributors || [];
    if (!selected.length) return FACET_DISTRIBUTORS;
    return FACET_DISTRIBUTORS.filter((d) =>
      selected.some((s: any) => s.name === d.key && s.selected),
    );
  }

  private getBestPrice(item: any): number | null {
    let best: number | null = null;
    for (const dist of this.facetDistributors) {
      const raw = item[`${dist.prefix}_price`];
      if (raw === null || raw === undefined || raw === '') continue;
      const price = parseFloat(String(raw).replace(/[$,]/g, ''));
      if (!isFinite(price) || price <= 0) continue;
      if (best === null || price < best) best = price;
    }
    return best;
  }

  // "barrel_length_in" -> "Barrel Length", "caliber" -> "Caliber"
  private prettifyAttributeKey(key: string): string {
    return key
      .replace(/_in$/, '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  private formatAttributeValue(key: string, value: any): string {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    // Keys suffixed with _in carry inches; show the unit on the value since
    // the group label drops it.
    if (typeof value === 'number') {
      return key.endsWith('_in') ? `${value}"` : String(value);
    }
    return String(value).trim();
  }

  // Facet groups are discovered from the result set: fixed taxonomy/buying
  // facets, plus one per attribute key present on any returned item.
  private buildFacetDefs(data: any[]): { key: string; label: string; numeric?: boolean }[] {
    const attributeKeys = new Set<string>();
    // Whether an attribute's options sort as numbers or as text depends on its
    // raw value type, not on whether the formatted label happens to start with
    // a digit — caliber values like "5.56 NATO" and "223 REM" are strings, so
    // parsing their label as a number would sort by magnitude instead of by
    // the caliber name (see rebuildFacets).
    const numericKeys = new Set<string>();
    for (const item of data) {
      const attributes = item?.attributes;
      if (!attributes || typeof attributes !== 'object') continue;
      for (const key of Object.keys(attributes)) {
        const raw = attributes[key];
        if (this.formatAttributeValue(key, raw)) {
          attributeKeys.add(key);
          const sample = Array.isArray(raw) ? raw[0] : raw;
          if (typeof sample === 'number') numericKeys.add(key);
        }
      }
    }

    const attributeDefs = [...attributeKeys]
      .map((key) => ({
        key: ATTRIBUTE_FACET_PREFIX + key,
        label: this.prettifyAttributeKey(key),
        numeric: numericKeys.has(key),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return [...LEADING_FACET_DEFS, ...attributeDefs, ...TRAILING_FACET_DEFS];
  }

  // A facet can be multi-valued for a single item (an item is "in stock at"
  // several distributors), so every extractor returns a list.
  private getItemFacetValues(item: any, groupKey: string): string[] {
    if (groupKey.startsWith(ATTRIBUTE_FACET_PREFIX)) {
      const attributeKey = groupKey.slice(ATTRIBUTE_FACET_PREFIX.length);
      const raw = item?.attributes?.[attributeKey];
      const values = Array.isArray(raw) ? raw : [raw];
      return values
        .map((v) => this.formatAttributeValue(attributeKey, v))
        .filter((v) => v !== '');
    }

    switch (groupKey) {
      case 'manufacturer':
        return item.manufacturer ? [item.manufacturer] : [];
      case 'category':
        return item.category ? [item.category] : [];
      case 'subcategory':
        return item.subcategory ? [item.subcategory] : [];
      case 'item_type':
        return item.item_type ? [item.item_type] : [];
      case 'stock':
        return this.facetDistributors
          .filter((d) => this.getQuantity(item, d.prefix) > 0)
          .map((d) => d.key);
      case 'availability':
        return this.facetDistributors.some(
          (d) => this.getQuantity(item, d.prefix) > 0,
        )
          ? ['in']
          : ['out'];
      case 'price': {
        const price = this.getBestPrice(item);
        if (price === null) return [];
        const bucket = PRICE_BUCKETS.find(
          (b) => price >= b.min && price < b.max,
        );
        return bucket ? [bucket.value] : [];
      }
      default:
        return [];
    }
  }

  private itemMatchesFacet(item: any, groupKey: string): boolean {
    const selected = this.selectedFacets[groupKey];
    if (!selected?.size) return true;
    return this.getItemFacetValues(item, groupKey).some((v) => selected.has(v));
  }

  // Counts for a group are computed against every OTHER active facet, so the
  // numbers shown reflect what selecting that option would actually yield.
  private itemMatches(item: any, exceptGroupKey?: string): boolean {
    for (const groupKey of Object.keys(this.selectedFacets)) {
      if (groupKey === exceptGroupKey) continue;
      if (!this.itemMatchesFacet(item, groupKey)) return false;
    }
    return true;
  }

  private facetOptionLabel(groupKey: string, value: string): string {
    if (groupKey === 'stock') {
      return (
        FACET_DISTRIBUTORS.find((d) => d.key === value)?.label ?? value
      );
    }
    if (groupKey === 'availability') {
      return value === 'in' ? 'In Stock' : 'Out of Stock';
    }
    if (groupKey === 'price') {
      return PRICE_BUCKETS.find((b) => b.value === value)?.label ?? value;
    }
    return value;
  }

  private rebuildFacets() {
    const data = this.sharedService.item_data || [];
    this.filteredItemCount = data.filter((item) =>
      this.itemMatches(item),
    ).length;

    const groups = this.buildFacetDefs(data).map((def) => {
      const counts = new Map<string, number>();
      for (const item of data) {
        if (!this.itemMatches(item, def.key)) continue;
        for (const value of this.getItemFacetValues(item, def.key)) {
          counts.set(value, (counts.get(value) || 0) + 1);
        }
      }

      const selected = this.selectedFacets[def.key] ?? new Set<string>();
      // A selected option must stay visible even when nothing else matches it,
      // otherwise the user has no way to switch it back off.
      for (const value of selected) {
        if (!counts.has(value)) counts.set(value, 0);
      }

      const options: FacetOption[] = [...counts.entries()].map(
        ([value, count]) => ({
          value,
          label: this.facetOptionLabel(def.key, value),
          count,
          selected: selected.has(value),
        }),
      );

      if (def.key === 'price') {
        const order = PRICE_BUCKETS.map((b) => b.value);
        options.sort((a, b) => order.indexOf(a.value) - order.indexOf(b.value));
      } else if (def.key === 'availability') {
        options.sort((a, b) => a.value.localeCompare(b.value));
      } else if (def.numeric) {
        options.sort((a, b) => parseFloat(a.label) - parseFloat(b.label));
      } else {
        options.sort((a, b) => a.label.localeCompare(b.label));
      }

      // Truncate to the most common values but render them in the group's
      // natural order, so expanding grows the list without resorting it.
      const expanded = this.expandedFacets.has(def.key);
      let visibleOptions = options;
      if (!expanded && options.length > FACET_OPTION_LIMIT) {
        const mostCommon = new Set(
          [...options]
            .sort((a, b) => b.count - a.count)
            .slice(0, FACET_OPTION_LIMIT)
            .map((o) => o.value),
        );
        visibleOptions = options.filter(
          (o) => mostCommon.has(o.value) || o.selected,
        );
      }

      return {
        key: def.key,
        label: def.label,
        options,
        visibleOptions,
        hiddenCount: options.length - visibleOptions.length,
        expanded,
        collapsed: this.collapsedFacets.has(def.key),
        selectedCount: selected.size,
      };
    }).filter((group) => group.options.length > 1 || group.selectedCount > 0);

    // On a fresh result set, open the first group and leave the rest closed so
    // a long attribute list doesn't bury the sidebar.
    if (this.applyDefaultCollapse) {
      groups.forEach((group, index) => {
        if (index === 0) return;
        this.collapsedFacets.add(group.key);
        group.collapsed = true;
      });
      this.applyDefaultCollapse = false;
    }

    this.facetGroups = groups;
  }

  // AG Grid external filter hooks - arrow properties so `this` stays bound.
  isExternalFilterPresent = (): boolean => this.hasActiveFacets;

  doesExternalFilterPass = (node: any): boolean =>
    !node?.data || this.itemMatches(node.data);

  async onGridReady(params: any) {
    this.gridApi = params.api;
    this.updateColumnVisibility();
    this.sharedService.item_data = [...this.sharedService.item_data];

    if (!this.agGridService.hasCachedSettingsFor(ComponentNames.vendor_dashboard)) {
      await this.agGridService.loadSavedTableSettings(
        ComponentNames.vendor_dashboard,
      );
    }
    this.applyCurrentSessionPreferences();
  }

  // preference functions
  storeCurrentSessionFilters() {
    this.agGridService.storeCurrentSessionFilters(
      ComponentNames.vendor_dashboard,
      this.gridApi.getFilterModel(),
    );
  }
  storeCurrentSessionColumnConfig() {
    this.agGridService.storeCurrentSessionColumnConfig(
      ComponentNames.vendor_dashboard,
      this.gridApi?.getColumnState(),
    );
  }
  applyCurrentSessionPreferences() {
    let component: any = this.agGridService.current_session_filtering.find(
      (component) =>
        component.component_name === ComponentNames.vendor_dashboard,
    );
    if (component && !isObjectEmpty(component.filters)) {
      this.gridApi.setFilterModel(component.filters);
    }
    component = this.agGridService.current_session_column_config.find(
      (component) =>
        component.component_name === ComponentNames.vendor_dashboard,
    );
    if (component && !isObjectEmpty(component.column_config)) {
      this.gridApi?.applyColumnState({
        state: component.column_config,
        applyOrder: true,
      });
    }
  }
  saveCurrentTablePreferencesClick() {
    const filters = this.gridApi.getFilterModel();
    const column_state = this.gridApi?.getColumnState();
    this.agGridService.saveCurrentTableSettings(
      ComponentNames.vendor_dashboard,
      filters,
      column_state,
    );
  }

  onSearchTermEnter(event: any) {
    if (!this.search_term?.trim()) return;
    const searchTerm = this.search_term;
    this.item_upc = '';
    this.activeBrowse = null;
    this.resetFacets();
    this.clearQuantitiesAndPrices();
    this.sharedService.openStatusSpinner(
      'Gathering live Vendor for the selected item',
    );
    this.subscriptions.add(
      this.sharedService
        .getInventoryMatchingSearchTerm(this.search_term.toUpperCase())
        .subscribe({
          next: (live_inventory) => {
            this.sharedService.closeStatusSpinner();
            this.search_term = '';
            if (!live_inventory) {
              this.sharedService.raiseErrorToast(
                'No vendor data found for this item. Please check the UPC and try again.',
              );
            } else {
              this.sharedService.item_data = live_inventory as LiveVendorData[];
              this.sharedService.item_data = [...this.sharedService.item_data];
              this.activeBrowse = this.sharedService.item_data.length > 1 ? { type: 'search', value: searchTerm } : null;
              this.updateColumnVisibility();
              // If there's only one item, select it and show thumbnail
              if (this.sharedService.item_data.length === 1) {
                const item = this.sharedService.item_data[0];
                this.updateDistributorData(item);
                this.fetchThumbnail(
                  item.upc,
                  item.item_number,
                  item.manufacturer,
                  item.mfg_part_number,
                  item.origin,
                  item.imageSource,
                );
              } else {
                this.itemThumbnailUrls = [];
                this.currentImageIndex = 0;
              }
            }
          },
          error: (err) => {
            this.sharedService.closeStatusSpinner();
            this.sharedService.raiseErrorToast(
              'Error fetching vendor data: ' + err.message,
            );
          },
        }),
    );
  }
  onItemUpcEnter(event: any) {
    this.search_term = '';
    this.activeBrowse = null;
    this.resetFacets();
    if (this.item_upc === '') {
      this.clearQuantitiesAndPrices();
      this.sharedService.item_data = [];
      return;
    }
    // const is_duplicate = this.checkIfDuplicate(this.item_upc);
    const is_number = numberOnlyValidator(this.item_upc);
    // if (is_number && !is_duplicate) {
    if (is_number) {
      const upcValue = this.item_upc;
      this.clearQuantitiesAndPrices();
      this.sharedService.item_data = [];
      this.sharedService.openStatusSpinner(
        'Gathering live Vendor for the selected item',
      );
      this.subscriptions.add(
        this.sharedService.getVendorData(this.item_upc).subscribe({
          next: (live_inventory) => {
            this.sharedService.closeStatusSpinner();
            if (!live_inventory) {
              this.sharedService.raiseErrorToast(
                'No vendor data found for this item. Please check the UPC and try again.',
              );
            } else {
              this.sharedService.item_data.push(live_inventory);
              this.sharedService.item_data = [...this.sharedService.item_data];
              this.updateDistributorData(live_inventory);
              this.updateColumnVisibility();
              this.fetchThumbnail(
                live_inventory.upc,
                live_inventory.item_number,
                live_inventory.manufacturer,
                live_inventory.mfg_part_number,
                live_inventory.origin,
                live_inventory.imageSource,
              );
            }
          },
          error: (err) => {
            this.sharedService.closeStatusSpinner();
            this.sharedService.raiseErrorToast(
              'Error fetching vendor data: ' + err.message,
            );
          },
        }),
      );
    }
  }
  onRowClicked(event: RowClickedEvent): void {
    this.rowClickSubject.next(event); // had to do this method because of endless loop with change detection
  }
  // Call this after updating distributor data
  updateDistributorData(live_inventory: LiveVendorData): void {
    this.clearQuantitiesAndPrices();

    // Always fetch fresh from company_info to avoid ngOnInit race conditions
    const currentDistributors =
      this.sharedService.company_info?.live_inv_selected_distributors || [];

    const updatedDistributors = currentDistributors.map((dist) => {
      let prefix = dist.name;
      if (dist.name === 'allen_arms_tactical') prefix = 'allen_arms';
      if (dist.name === 'cssi') prefix = 'chattanooga';
      return {
        ...dist,
        quantity: this.getQuantity(live_inventory, prefix),
        price: live_inventory[`${prefix}_price`],
      };
    });

    this.distributorService.live_inv_selected_distributors = [
      ...updatedDistributors,
    ];

    const displayData = updatedDistributors
      .filter((dist) => dist.selected === true)
      .map((dist) => ({
        name: dist.name,
        quantity: dist.quantity || 0,
        price: dist.price,
      }));

    // Deduplicate by name
    const uniqueMap = new Map();
    displayData.forEach((item) => {
      if (!uniqueMap.has(item.name)) {
        uniqueMap.set(item.name, item);
      }
    });
    this.distributorDisplayData = Array.from(uniqueMap.values());

    this.sortDistributors();
  }

  copyUpcBadge(): void {
    const upc = this.selectedRow?.upc || this.item_upc;
    if (!upc) return;
    navigator.clipboard.writeText(upc);
    this.sharedService.raiseSuccessToast('UPC copied to clipboard');
  }

  isDistributorActive(name: string): boolean {
    const list =
      this.sharedService.company_info?.live_inv_selected_distributors || [];
    return list.some((dist) => dist.name === name && dist.selected === true);
  }

  getQuantity(item: any, distributorPrefix: string): number {
    const qty = item[`${distributorPrefix}_available_qty`];
    if (qty === null || qty === undefined) return 0;
    if (typeof qty === 'number') return qty;
    if (typeof qty === 'string' && qty.includes('+')) {
      return parseInt(qty.replace('+', ''), 10) || 0;
    }
    return parseInt(qty, 10) || 0;
  }
  sortDistributors() {
    this.distributorDisplayData.sort((a, b) => {
      const qtyA = a.quantity;
      const qtyB = b.quantity;
      if (qtyA !== qtyB) return qtyB - qtyA; // Higher quantity first

      const priceA = a.price;
      const priceB = b.price;
      if (!priceA && !priceB) return 0;
      if (!priceA) return 1;
      if (!priceB) return -1;
      const numPriceA = priceA ? parseFloat(priceA.replace('$', '')) : 0;
      const numPriceB = priceB ? parseFloat(priceB.replace('$', '')) : 0;
      return numPriceB - numPriceA; // Higher price first
    });
  }
  getLatestItemQty(distributorName: string): number {
    const latestItem =
      this.sharedService.item_data[this.sharedService.item_data.length - 1];
    if (!latestItem) return 0;
    const qty = latestItem[`${distributorName}_available_qty`];
    return qty
      ? qty.includes('+')
        ? parseInt(qty.replace('+', ''), 10)
        : parseInt(qty, 10)
      : 0;
  }
  getLatestItemPrice(distributorName: string): string | null {
    const latestItem =
      this.sharedService.item_data[this.sharedService.item_data.length - 1];
    return latestItem ? latestItem[`${distributorName}_price`] : null;
  }
  checkIfDuplicate(upc: string) {
    if (this.sharedService.item_data.some((item) => item.upc === upc)) {
      this.sharedService.raiseWarningToast(
        'Item has already added to the table',
      );
      this.flashDuplicateRow(upc);
      return true;
    } else {
      return false;
    }
  }
  flashDuplicateRow(upc: string) {
    let rowNodeToFlash = null;

    this.gridApi.forEachNode((node) => {
      if (node.data.upc === upc) {
        rowNodeToFlash = node;
      }
    });

    if (rowNodeToFlash) {
      this.gridApi.flashCells({ rowNodes: [rowNodeToFlash] });
    }
  }
  clearQuantitiesAndPrices() {
    this.distributorService.live_inv_selected_distributors =
      this.distributorService.live_inv_selected_distributors.map((dist) => ({
        ...dist,
        quantity: null, // or undefined
        price: null, // or undefined
      }));
  }
  clearTableClick() {
    this.sharedService.item_data = [];
    this.activeBrowse = null;
    this.resetFacets();
  }
  openRequestListsClick() {
    this.sharedService.changeRoute('inventory-request-lists');
  }
  openAppDashboardClick() {
    this.sharedService.changeRoute('home');
  }
  updateColumnVisibility() {
    if (!this.gridApi) return;
    const selectedDistributors =
      this.distributorService.live_inv_selected_distributors;
    const distributorColumns: { [key: string]: string[] } = {
      davidsons: ['davidsons_available_qty', 'davidsons_price'],
      rsr_group: ['rsr_group_available_qty', 'rsr_group_price'],
      zanders: ['zanders_available_qty', 'zanders_price'],
      lipseys: ['lipseys_available_qty', 'lipseys_price'],
      chattanooga: ['chattanooga_available_qty', 'chattanooga_price'],
      allen_arms_tactical: ['allen_arms_available_qty', 'allen_arms_price'],
    };

    for (const distributor in distributorColumns) {
      const columns = distributorColumns[distributor];
      const isVisible = selectedDistributors.some(
        (d) => d.name === distributor && d.selected,
      );
      columns.forEach((colId: string) => {
        this.gridApi.setColumnsVisible([colId], isVisible);
      });
    }
  }
  // Right-Click Menu Functions
  onRightClick(event: MouseEvent, distributor: any) {
    event.preventDefault();
    event.stopPropagation();
    console.log(distributor);
    this.selectedDistributor = distributor;

    // Get the vendor-panel element that was clicked
    const vendorPanel = event.currentTarget as HTMLElement;
    const panelRect = vendorPanel.getBoundingClientRect();

    // Assume menu width (adjust this based on your CSS or measure dynamically)
    const menuWidth = 200; // Matches min-width in your CSS
    const offset = 5; // Small gap between menu and vendor-panel (optional)

    // Position menu to the left of the vendor-panel.
    // Use viewport-relative coords (clientX/clientY) because the menu is position: fixed.
    this.menuLeft = Math.max(panelRect.left - menuWidth - offset, 0); // Prevent going off left edge
    this.menuTop = Math.min(event.clientY, window.innerHeight - 120); // Keep top at click point, adjust for menu height

    this.showMenu = true;
  }

  async menuAction(action: string, upc: string) {
    console.warn(this.selectedDistributor);
    if (!this.selectedDistributor) {
      this.sharedService.raiseWarningToast('No distributor selected');
      return;
    }

    switch (action) {
      case 'Get Pricing':
        try {
          this.sharedService.openStatusSpinner('Gathering Competitor Pricing');

          const pricingPromise =
            await this.sharedService.getCompetitorPricingAsync(upc);
          const timeoutPromise = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Request timed out')), 30000),
          );
          const pricing: CompetitorPricing[] | null = await Promise.race([
            pricingPromise,
            timeoutPromise,
          ]);

          if (!pricing || pricing[0].price === undefined) {
            this.sharedService.raiseInfoToast(
              `No valid pricing data found for UPC ${upc}`,
            );
            console.log(`No valid pricing returned for UPC ${upc}`);
          } else {
            console.warn('Pricing retrieved:', pricing);
            this.sharedService.openCompetitorPricingModal(
              pricing,
              this.selectedDistributor['price'],
            );
            this.sharedService.closeStatusSpinner();
          }
        } catch (error) {
          this.sharedService.closeStatusSpinner();

          let errorMessage = 'Failed to retrieve competitor pricing';
          if (error instanceof Error) {
            errorMessage = error.message;
            if (error.message === 'Request timed out') {
              this.sharedService.raiseErrorToast(
                `Pricing request timed out for UPC ${upc}`,
              );
            } else if (error.message.includes('No available deals found')) {
              this.sharedService.raiseInfoToast(
                `No deals available for UPC ${upc}`,
              );
            } else {
              this.sharedService.raiseErrorToast(errorMessage);
            }
          } else if ((error as any).status === 404) {
            this.sharedService.raiseInfoToast(`No deals found for UPC ${upc}`);
          } else if ((error as any).status) {
            this.sharedService.raiseErrorToast(
              `Server error (${(error as any).status}) for UPC ${upc}`,
            );
          } else {
            this.sharedService.raiseErrorToast(errorMessage);
          }
          console.error('Error fetching pricing:', error);
        }
        break;

      case 'Refresh':
        console.log(`Refreshing ${this.selectedDistributor.name}`);
        try {
          await this.refreshDistributorData(this.selectedDistributor);
          this.sharedService.raiseSuccessToast(
            `Refreshed ${this.selectedDistributor.name} successfully`,
          );
        } catch (error) {
          this.sharedService.raiseErrorToast(
            `Failed to refresh ${this.selectedDistributor.name}`,
          );
          console.error('Refresh error:', error);
        }
        break;

      case 'Remove':
        console.log(`Removing ${this.selectedDistributor.name}`);
        try {
          await this.removeDistributor(this.selectedDistributor);
          this.sharedService.raiseSuccessToast(
            `Removed ${this.selectedDistributor.name} successfully`,
          );
        } catch (error) {
          this.sharedService.raiseErrorToast(
            `Failed to remove ${this.selectedDistributor.name}`,
          );
          console.error('Remove error:', error);
        }
        break;

      case 'Copy UPC':
        if (upc) {
          navigator.clipboard.writeText(upc);
          this.sharedService.raiseSuccessToast('UPC copied to clipboard');
        }
        break;
      case 'Copy Price':
        if (this.selectedDistributor && this.selectedDistributor.price) {
          navigator.clipboard.writeText(
            this.selectedDistributor.price.toString(),
          );
          this.sharedService.raiseSuccessToast('Price copied to clipboard');
        } else {
          this.sharedService.raiseInfoToast('No price available to copy');
        }
        break;
      default:
        console.warn(`Unknown action: ${action}`);
        this.sharedService.raiseWarningToast(
          `Action '${action}' not recognized`,
        );
        break;
    }

    this.showMenu = false;
    this.selectedDistributor = null;
  }

  refreshDistributorData(distributor: any) {
    // Assuming sharedService.getVendorData can fetch data for a specific distributor
    this.sharedService.openStatusSpinner(
      `Refreshing data for ${distributor.name}`,
    );
    this.subscriptions.add(
      this.sharedService.getVendorData(this.item_upc).subscribe({
        next: (live_inventory) => {
          this.sharedService.closeStatusSpinner();
          if (live_inventory) {
            this.updateDistributorData(live_inventory);
            this.sharedService.item_data = [...this.sharedService.item_data]; // Trigger grid refresh
            this.updateColumnVisibility();
          } else {
            this.sharedService.raiseErrorToast(
              `No updated data found for ${distributor.name}`,
            );
          }
        },
        error: (err) => {
          this.sharedService.closeStatusSpinner();
          this.sharedService.raiseErrorToast(
            `Error refreshing ${distributor.name}: ${err.message}`,
          );
        },
      }),
    );
  }

  removeDistributor(distributor: any) {
    this.distributorService.live_inv_selected_distributors =
      this.distributorService.live_inv_selected_distributors.filter(
        (d) => d.name !== distributor.name,
      );
    this.updateColumnVisibility(); // Update grid columns after removal
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.context-menu') && !target.closest('.vendor-panel')) {
      this.showMenu = false; // Hide menu if clicking outside
      this.selectedDistributor = null; // Clear selection
    }
  }

  fetchThumbnail(
    upc: string,
    itemNumber?: string,
    manufacturer?: string,
    mfgPartNumber?: string,
    origin?: string,
    imageSource?: string,
  ) {
    if (!upc && !itemNumber && !mfgPartNumber) {
      this.itemThumbnailUrls = [];
      this.currentImageIndex = 0;
      this.isThumbnailLoading = false;
      return;
    }

    this.isThumbnailLoading = true;
    this.subscriptions.add(
      this.sharedService
        .getInventoryThumbnailUrl(
          upc,
          itemNumber,
          manufacturer,
          mfgPartNumber,
          origin,
          imageSource,
        )
        .subscribe({
          next: (res) => {
            this.itemThumbnailUrls = res.thumbnailUrls || [];
            this.currentImageIndex = 0;
            this.isThumbnailLoading = false;
          },
          error: (err) => {
            console.warn('Failed to fetch thumbnail:', err);
            this.itemThumbnailUrls = [];
            this.currentImageIndex = 0;
            this.isThumbnailLoading = false;
          },
        }),
    );
  }

  prevImage(event: Event) {
    event.stopPropagation();
    if (this.itemThumbnailUrls.length > 0) {
      this.currentImageIndex =
        (this.currentImageIndex - 1 + this.itemThumbnailUrls.length) %
        this.itemThumbnailUrls.length;
    }
  }

  nextImage(event: Event) {
    event.stopPropagation();
    if (this.itemThumbnailUrls.length > 0) {
      this.currentImageIndex =
        (this.currentImageIndex + 1) % this.itemThumbnailUrls.length;
    }
  }

  toggleLightbox(open: boolean) {
    this.isLightboxOpen = open;
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (this.isLightboxOpen) {
      if (event.key === 'Escape') this.toggleLightbox(false);
      if (event.key === 'ArrowLeft') this.prevImage(event);
      if (event.key === 'ArrowRight') this.nextImage(event);
    }
  }

  onSearchClick() {
    if (this.item_upc) {
      this.onItemUpcEnter(null);
    } else if (this.search_term) {
      this.onSearchTermEnter(null);
    } else {
      this.sharedService.raiseErrorToast('Please enter an UPC or search term');
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // right click copy functions
  onCellContextMenu(event: CellContextMenuEvent) {
    this.contextMenuService.onCellContextMenu(event);
  }
  onMenuMouseEnter() {
    this.contextMenuService.onMenuMouseEnter();
  }
  onMenuMouseLeave() {
    this.contextMenuService.onMenuMouseLeave();
  }
  copyCellValue() {
    this.contextMenuService.copyCellValue();
  }

  addToWantsAlert() {
    const state = (this.contextMenuService as any).menuState.getValue();
    const rowData = state.rowData;
    if (!rowData) {
      this.sharedService.raiseWarningToast('No row selected');
      return;
    }
    const itemData = {
      upc: rowData.upc || '',
      name: rowData.mfg_part_number || rowData.manufacturer || '',
      description: rowData.desc1 || rowData.desc2 || '',
    };
    this.sharedService.openInventoryOptionsModal('alert', 'Add', null, itemData);
    this.contextMenuService.onMenuMouseLeave();
  }

  addToAutoPurchase() {
    const state = (this.contextMenuService as any).menuState.getValue();
    const rowData = state.rowData;
    if (!rowData) {
      this.sharedService.raiseWarningToast('No row selected');
      return;
    }
    const itemData = {
      upc: rowData.upc || '',
      name: rowData.mfg_part_number || rowData.manufacturer || '',
      description: rowData.desc1 || rowData.desc2 || '',
    };
    this.sharedService.openInventoryOptionsModal('auto_purchase', 'Add', null, itemData);
    this.contextMenuService.onMenuMouseLeave();
  }
  onMyFavoritesClick() {
    this.search_term = '';
    this.item_upc = '';
    this.activeBrowse = null;
    this.clearQuantitiesAndPrices();
    this.sharedService.openStatusSpinner('Gathering your favorite items');

    this.subscriptions.add(
      this.sharedService.getFavoriteInventoryItems().subscribe({
        next: (items) => {
          this.sharedService.closeStatusSpinner();
          if (!items.length) {
            this.sharedService.raiseInfoToast('No favorites yet — star an item to add one.');
            this.sharedService.item_data = [];
            this.resetFacets();
            return;
          }

          this.sharedService.item_data = [...items];
          this.activeBrowse = { type: 'favorites', value: 'My Favorites' };
          // Facets are derived from item_data, so they must be rebuilt only
          // after the fresh results land — resetting first would compute
          // them against whatever was previously loaded.
          this.resetFacets();
          this.updateColumnVisibility();

          if (items.length === 1) {
            const item = items[0];
            this.updateDistributorData(item);
            this.fetchThumbnail(
              item.upc,
              item.item_number,
              item.manufacturer,
              item.mfg_part_number,
              item.origin,
              item.imageSource,
            );
          } else {
            this.itemThumbnailUrls = [];
            this.currentImageIndex = 0;
          }
        },
        error: (err) => {
          this.sharedService.closeStatusSpinner();
          this.sharedService.raiseErrorToast(
            'Error fetching favorites: ' + err.message,
          );
        },
      }),
    );
  }
}
